import { z } from 'zod';
/**
 * Lógica compartida (backend + frontend) para evaluar si una muestra de
 * laboratorio está cumpliendo con la resistencia esperada según la curva
 * teórica (ASTM C-150 Type I) y el F'C objetivo.
 *
 * Reglas:
 *   ratio = kg/cm²_real / (pct_teorico × F'C)
 *     - ratio >= 0.95 → 'cumple'       (verde)
 *     - 0.85 <= ratio < 0.95 → 'marginal' (amarillo)
 *     - ratio < 0.85 → 'incumple'      (rojo)
 *   sin mediciones → 'sin_dato'        (gris)
 *
 * El umbral 0.95 deja un margen del 5% para variabilidad normal del
 * ensayo (tolerancia ASTM). 0.85 es el límite donde el laboratorio
 * típicamente reporta como "no conforme" y dispara revisión del lote.
 *
 * Una MUESTRA se evalúa con el PEOR ensayo (worst-case): si cualquier
 * ensayo está incumpliendo, la muestra completa está incumpliendo.
 */
export const EVALUACIONES_CUMPLIMIENTO = ['cumple', 'marginal', 'incumple', 'sin_dato'];
export const EvaluacionCumplimientoSchema = z.enum(EVALUACIONES_CUMPLIMIENTO);
// Umbrales por DEFAULT (los del catálogo "lab.umbral_*"). Si la app tiene
// configurados otros, pasarlos como tercer/cuarto parámetro a las funciones.
export const UMBRAL_CUMPLE = 0.95;
export const UMBRAL_MARGINAL = 0.85;
export const UMBRALES_CUMPLIMIENTO_DEFAULT = {
    cumple: UMBRAL_CUMPLE,
    marginal: UMBRAL_MARGINAL,
};
/**
 * Interpola linealmente el % de resistencia esperado a una edad dada,
 * usando los puntos de la curva teórica como anclas. Si la edad coincide
 * exacta con un punto, devuelve ese valor sin interpolación.
 */
export function interpolarCurvaTeorica(puntos, edad) {
    if (puntos.length === 0)
        return 0;
    const exacto = puntos.find((p) => p.edad_dias === edad);
    if (exacto)
        return exacto.pct_resistencia;
    const ordenados = [...puntos].sort((a, b) => a.edad_dias - b.edad_dias);
    const anteriores = ordenados.filter((p) => p.edad_dias < edad);
    const posteriores = ordenados.filter((p) => p.edad_dias > edad);
    if (anteriores.length === 0)
        return posteriores[0]?.pct_resistencia ?? 0;
    if (posteriores.length === 0)
        return anteriores.at(-1)?.pct_resistencia ?? 0;
    const a = anteriores.at(-1);
    const b = posteriores[0];
    const t = (edad - a.edad_dias) / (b.edad_dias - a.edad_dias);
    return a.pct_resistencia + t * (b.pct_resistencia - a.pct_resistencia);
}
/**
 * Evalúa un único ensayo contra la curva teórica.
 */
export function evaluarEnsayoCumplimiento(ensayo, fcObjetivo, curva, umbrales = UMBRALES_CUMPLIMIENTO_DEFAULT) {
    if (ensayo.resistencia_kg_cm2_promedio === null)
        return 'sin_dato';
    if (fcObjetivo <= 0)
        return 'sin_dato';
    const pctTeorico = interpolarCurvaTeorica(curva, ensayo.edad_dias);
    if (pctTeorico <= 0)
        return 'sin_dato';
    const ratio = ensayo.resistencia_kg_cm2_promedio / (pctTeorico * fcObjetivo);
    if (ratio >= umbrales.cumple)
        return 'cumple';
    if (ratio >= umbrales.marginal)
        return 'marginal';
    return 'incumple';
}
/**
 * Evalúa el cumplimiento agregado de una muestra: peor caso de sus ensayos.
 * Si ningún ensayo tiene datos → 'sin_dato'.
 * Cualquier incumple → 'incumple'.
 * Cualquier marginal (sin incumple) → 'marginal'.
 * Todos cumplen → 'cumple'.
 */
export function evaluarMuestraCumplimiento(ensayos, fcObjetivo, curva, umbrales = UMBRALES_CUMPLIMIENTO_DEFAULT) {
    const conDatos = ensayos.filter((e) => e.resistencia_kg_cm2_promedio !== null);
    if (conDatos.length === 0)
        return 'sin_dato';
    let peor = 'cumple';
    for (const e of conDatos) {
        const v = evaluarEnsayoCumplimiento(e, fcObjetivo, curva, umbrales);
        if (v === 'incumple')
            return 'incumple'; // short-circuit: peor posible
        if (v === 'marginal')
            peor = 'marginal';
    }
    return peor;
}
//# sourceMappingURL=evaluacion-resistencia.js.map